/**
 * The ONE worker-health vocabulary, upstream of everything that uses it.
 *
 * `RoutePolicyEngine` compares `listener.workerHealth !== 'healthy'` and demotes
 * `translated_source` below `original_source` when that is true — the §7.6
 * fallback that keeps the ORIGINAL audible through every degraded mode. Four
 * things now speak this vocabulary: the audio-router types, the Redux slice, the
 * viewer-context route's runtime validation, and the hook that dispatches it.
 * One declaration, so the four cannot drift.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * WHY FOUR VALUES WHEN THE DATABASE ONLY EVER EMITS TWO
 *
 * `translation_get_viewer_health` (migration 20261106) emits `healthy` and
 * `unavailable` and nothing else. `delayed` and `reconnecting` remain in the
 * ENGINE contract because the engine's ranking is written in terms of "not
 * healthy", and narrowing the type would quietly delete a distinction the policy
 * is expressed against.
 *
 * They have NO PRODUCER. `worker-translateaiz/translateaiz/room_coordinator.py`
 * has exactly one `set_route_attribute` call site and it publishes a HARDCODED
 * `state="active"`; `SessionState.DELAYED` and `SessionState.RECONNECTING` are
 * referenced nowhere in it. Deriving health from route envelopes would therefore
 * have produced a guard that is present, correctly named, and structurally
 * unable to fire — this project's dominant defect class, and it has already
 * fired five times. `scripts/tests/test_live_translation_degraded_state_producer_absent.py`
 * is the guard on that absence; it fails the moment a real transition appears,
 * which is the right moment to decide what the new state means for health.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * THE FAIL-CLOSED DIRECTION
 *
 * `unavailable` is the REFUSING value: it forces the original. Every default,
 * every reset, every parse failure and every read failure resolves to it, and
 * nothing in this system may default to `healthy`.
 */

/**
 * Declared as a tuple so the type is DERIVED from the runtime list.
 *
 * A hand-written union beside a hand-written array is two declarations of one
 * fact, and the validator would go on accepting a value the type had dropped.
 */
export const WORKER_HEALTH_VALUES = [
  'healthy',
  'delayed',
  'reconnecting',
  'unavailable',
] as const;

export type WorkerHealth = (typeof WORKER_HEALTH_VALUES)[number];

/** The value everything falls back to. Never `healthy`, in any code path. */
export const WORKER_HEALTH_FAIL_CLOSED: WorkerHealth = 'unavailable';

/** The subset `translation_get_viewer_health` is documented to emit. */
export const SQL_EMITTED_WORKER_HEALTH_VALUES = ['healthy', 'unavailable'] as const;

const WORKER_HEALTH_SET: ReadonlySet<string> = new Set(WORKER_HEALTH_VALUES);

/** Membership, not shape. An unknown string is refused, never coerced. */
export function isWorkerHealth(value: unknown): value is WorkerHealth {
  return typeof value === 'string' && WORKER_HEALTH_SET.has(value);
}
